<template>
  <div class="fault-report">
    <!-- 待处理：上报甲方补充信息 -->
    <template v-if="status === 'pending'">
      <el-divider content-position="left">上报单补充信息</el-divider>
      <el-form ref="reportFormRef" :model="reportForm" :rules="reportRules" label-width="110px">
        <el-form-item label="故障原因" prop="faultDesc">
          <el-input
            v-model="reportForm.faultDesc"
            type="textarea"
            :rows="4"
            placeholder="默认带出系统记录，可在此修改；仅用于生成上报文件，不修改故障单中的原始记录"
          />
        </el-form-item>
        <el-form-item v-if="hasFaultPhotoIds" label="现场照片">
          <ImageThumbViewer
            :urls="faultPhotoThumbUrls"
            :thumb-loading="faultPhotosLoading"
            :thumb-loading-count="faultPhotoIdsCount"
            variant="compact"
            preview-title="查看照片"
            preview-width="640px"
          />
        </el-form-item>

        <el-form-item label="补充现场照片">
          <ImageUploader
            v-model="reportForm.reportExtraImages"
            tip="选填，最多5张，支持 jpg/png；将排在上方现场图之后写入上报文件"
          />
        </el-form-item>

        <el-form-item label="建议方案" prop="proposed">
          <el-input
            v-model="reportForm.proposed"
            type="textarea"
            :rows="3"
            placeholder="填写建议方案（将写入上报文件）"
          />
        </el-form-item>
        <el-form-item label="应急处置" prop="emergency">
          <el-input
            v-model="reportForm.emergency"
            type="textarea"
            :rows="3"
            placeholder="填写应急处置说明（将写入上报文件）"
          />
        </el-form-item>

        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item label="维保人员" prop="wbUser">
              <el-input v-model="reportForm.wbUser" placeholder="填写姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机号" label-width="70px" prop="wbUserPhone">
              <el-input v-model="reportForm.wbUserPhone" placeholder="填写维保人员手机号" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item label="消防科人员" prop="xfkUser">
              <el-input v-model="reportForm.xfkUser" placeholder="填写姓名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="手机号" label-width="70px" prop="xfkUserPhone">
              <el-input v-model="reportForm.xfkUserPhone" placeholder="填写消防科人员手机号" maxlength="20" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </template>

    <!-- 已上报：只读展示 -->
    <template v-else-if="status === 'reported'">
      <el-divider content-position="left">
        <span class="divider-label">上报信息</span>
        <el-tag type="info" size="mini" style="margin-left:8px">
          上报时间：{{ formatDateTime(fault.reportTime) }}
        </el-tag>
      </el-divider>
      <el-form label-width="110px" class="readonly-form">
        <el-form-item label="故障原因">
          <span class="readonly-text desc-value--multiline">{{ formatFaultDesc(reportContentForRead.faultDesc || displayFault.faultDesc) }}</span>
        </el-form-item>
        <el-form-item v-if="hasFaultPhotoIds" label="现场照片">
          <ImageThumbViewer
            :urls="faultPhotoThumbUrls"
            :thumb-loading="faultPhotosLoading"
            :thumb-loading-count="faultPhotoIdsCount"
            variant="compact"
            preview-title="查看照片"
            preview-width="640px"
          />
        </el-form-item>

        <el-form-item v-if="hasReportExtraPhotoIds" label="补充现场照片">
          <ImageThumbViewer
            :urls="reportExtraPhotoThumbUrls"
            :thumb-loading="reportExtraPhotosLoading"
            :thumb-loading-count="reportExtraPhotoIdsCount"
            variant="compact"
            preview-title="查看照片"
            preview-width="640px"
          />
        </el-form-item>

        <el-form-item v-if="reportContentForRead.proposed" label="建议方案">
          <span class="readonly-text">{{ reportContentForRead.proposed }}</span>
        </el-form-item>
        <el-form-item v-if="reportContentForRead.emergency" label="应急处置">
          <span class="readonly-text">{{ reportContentForRead.emergency }}</span>
        </el-form-item>

        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item v-if="reportContentForRead.wbUser" label="维保人员">
              <span class="readonly-text">{{ reportContentForRead.wbUser }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item v-if="reportContentForRead.wbUserPhone" label="手机号" label-width="70px">
              <span class="readonly-text">{{ reportContentForRead.wbUserPhone }}</span>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10">
          <el-col :span="12">
            <el-form-item v-if="reportContentForRead.xfkUser" label="消防科人员">
              <span class="readonly-text">{{ reportContentForRead.xfkUser }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item v-if="reportContentForRead.xfkUserPhone" label="手机号" label-width="70px">
              <span class="readonly-text">{{ reportContentForRead.xfkUserPhone }}</span>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="上报文件">
          <el-button
            type="primary"
            plain
            size="small"
            icon="el-icon-download"
            :loading="downloading"
            @click="$emit('download-report')"
          >
            下载上报文件
          </el-button>
        </el-form-item>
      </el-form>
    </template>
  </div>
</template>

<script>
// 工具函数
import { formatDateTime } from '@/features/common/utils/dateTimeUtil.js';
import {
  buildReportContentForRead,
  formatFaultDesc,
  parseReportContentJson,
  serializeReportContent
} from '@/features/gnmk/fault/utils/faultHandleUtils.js';

export default {
  name: 'FaultReport',
  props: {
    status: {
      type: String,
      required: true,
      validator: (v) => ['pending', 'reported'].includes(v)
    },
    /** 与父组件共用同一引用，便于详情回填与 ImageUploader 双向绑定 */
    reportForm: {
      type: Object,
      required: true
    },
    fault: { type: Object, required: true },
    displayFault: { type: Object, required: true },
    faultPhotoThumbUrls: { type: Array, default: () => [] },
    faultPhotosLoading: { type: Boolean, default: false },
    faultPhotoIdsCount: { type: Number, default: 0 },
    hasFaultPhotoIds: { type: Boolean, default: false },
    reportExtraPhotoThumbUrls: { type: Array, default: () => [] },
    reportExtraPhotosLoading: { type: Boolean, default: false },
    reportExtraPhotoIdsCount: { type: Number, default: 0 },
    hasReportExtraPhotoIds: { type: Boolean, default: false },
    downloading: { type: Boolean, default: false }
  },
  data () {
    return {
      /** 上报 Word 补充项：预览/正式下载前必须校验；保存草稿仍允许不填 */
      reportRules: {
        faultDesc: [{ required: true, message: '请填写故障原因', trigger: 'blur' }],
        proposed: [{ required: true, message: '请填写建议方案', trigger: 'blur' }],
        emergency: [{ required: true, message: '请填写应急处置', trigger: 'blur' }],
        wbUser: [{ required: true, message: '请填写维保人员姓名', trigger: 'blur' }],
        wbUserPhone: [{ required: true, message: '请填写维保人员手机号', trigger: 'blur' }],
        xfkUser: [{ required: true, message: '请填写消防科人员姓名', trigger: 'blur' }],
        xfkUserPhone: [{ required: true, message: '请填写消防科人员手机号', trigger: 'blur' }]
      }
    };
  },
  computed: {
    reportContentForRead () {
      const p = parseReportContentJson(this.displayFault && this.displayFault.reportContent);
      return buildReportContentForRead(p);
    }
  },
  methods: {
    formatDateTime,
    formatFaultDesc,
    getSerializedReportContent () {
      return serializeReportContent(this.reportForm);
    },
    validateReportForm () {
      return new Promise((resolve, reject) => {
        this.$refs.reportFormRef.validate((valid) => {
          if (!valid) return reject(new Error('validation'));
          resolve();
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.desc-value--multiline {
  white-space: pre-wrap;
  line-height: 1.6;
  align-items: flex-start;
  padding-top: 2px;
  padding-bottom: 2px;
}

.readonly-form {
  .readonly-text {
    color: #303133;
    line-height: 1.6;
  }
}

.divider-label { font-weight: 500; }
</style>
